import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Phone, MessageCircle, MapPin, Check } from "lucide-react";

const HALL_CHARGE = 30000;

const TIERS = [
  {
    key: "silver",
    name: "Silver",
    rates: [
      { upTo: 100, rate: 370 },
      { upTo: 200, rate: 355 },
      { upTo: 500, rate: 340 },
    ],
    accent: "#8a8a8a",
    tagline: "Traditional South Indian feast",
    menu: [
      "Salt, Pickle, Kosambri",
      "2 Palya",
      "South Indian Starter",
      "Payasa",
      "Holige with Ghee or any South Indian Sweets",
      "Poori Saagu / Raagi Rotti (Uchal Chutney)",
      "Palav with Raita",
      "White Rice with Sambar, Rasam, Curd & Papad",
      "Banana, Water Bottle",
    ],
  },
  {
    key: "gold",
    name: "Gold",
    rates: [
      { upTo: 100, rate: 420 },
      { upTo: 200, rate: 405 },
      { upTo: 500, rate: 390 },
    ],
    accent: "#b8860b",
    tagline: "South Indian + North Indian classics",
    menu: [
      "Welcome Juice",
      "Salt, Pickle, Kosambri",
      "2 Palya",
      "1 South Indian Starter",
      "Payasa",
      "Holige with Ghee",
      "Kashi Halwa or any other Halwa",
      "Tawa Roti or Phulka with Mix Veg Kadai or Paneer Butter Masala",
      "Palav with Raita",
      "White Rice with Sambar/Pappu, Rasam, Curd & Papad",
      "Banana, Water Bottle",
      "Cup Ice Cream & Sweet Pan",
    ],
  },
  {
    key: "platinum",
    name: "Platinum",
    rates: [
      { upTo: 100, rate: 485 },
      { upTo: 200, rate: 470 },
      { upTo: 500, rate: 455 },
    ],
    accent: "#6B0F1A",
    featured: true,
    tagline: "Most popular — fusion spread",
    menu: [
      "Welcome Drinks — any Fruit Juice",
      "Wet Tissue",
      "Salt, Pickle, Kosambri",
      "2 Palya",
      "1 South Indian Starter",
      "1 Chinese Starter",
      "Payasa",
      "Holige with Ghee",
      "Kashi Halwa or any other Halwa",
      "Mini Masala Dosa with Chutney",
      "Tawa Roti or Phulka with Mix Veg Kadai or Paneer Butter Masala",
      "Palav with Raita",
      "White Rice with Sambar/Pappu, Rasam, Curd & Papad",
      "Banana, Water Bottle",
      "Ice Cream, Sweet Pan",
    ],
  },
  {
    key: "diamond",
    name: "Diamond",
    rates: [
      { upTo: 100, rate: 550 },
      { upTo: 200, rate: 535 },
      { upTo: 500, rate: 520 },
    ],
    accent: "#2b2b2b",
    tagline: "Royal premium experience",
    menu: [
      "Welcome Drinks — any Special Milk Shake",
      "1 Welcome Starter",
      "Any 1 Soup",
      "Wet Tissue",
      "Salt, Pickle, Kosambri",
      "2 Palya",
      "1 South Indian Starter",
      "1 Chinese Starter",
      "Payasa",
      "Holige with Ghee & Milk",
      "Any 1 North Indian or 1 Bengali Sweet",
      "Mini Masala Dosa or Idli with Chutney",
      "Tawa Roti or Phulka with Mix Veg Kadai or Paneer Butter Masala",
      "Palav with Raita",
      "White Rice with Dal Fry, Sambar/Pappu, Rasam, Curd & Papad",
      "Banana, Water Bottle",
      "Special Tissue, Finger Bowl",
      "Ice Cream, Sweet Pan",
    ],
  },
];

const pickRate = (tier, guests) =>
  tier.rates.find((b) => guests <= b.upTo) || tier.rates[tier.rates.length - 1];

const bracketLabel = (guests) => {
  if (guests <= 100) return "50 – 100 guests";
  if (guests <= 200) return "101 – 200 guests";
  return "201 – 500 guests";
};

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

export default function PartyHallCalculator() {
  const [guests, setGuests] = useState(50);

  const computed = useMemo(
    () =>
      TIERS.map((t) => {
        const rate = pickRate(t, guests).rate;
        const food = guests * rate;
        const total = food + HALL_CHARGE;
        const perHead = total / guests;
        return { ...t, rate, food, total, perHead };
      }),
    [guests]
  );

  const bracket = bracketLabel(guests);

  return (
    <div className="pad-root">
      {/* HERO */}
      <header className="pad-hero" data-testid="pad-hero">
        <div className="pad-hero-overlay" />
        <div className="pad-hero-content">
          <div className="pad-brand">
            <div className="pad-brand-mark">
              <svg viewBox="0 0 100 100" aria-hidden="true">
                <circle cx="50" cy="50" r="46" fill="none" stroke="#F5E9D7" strokeWidth="1.5" />
                <circle cx="50" cy="50" r="34" fill="none" stroke="#F5E9D7" strokeWidth="1" />
                <text
                  x="50"
                  y="56"
                  textAnchor="middle"
                  fontFamily="'Cormorant Garamond', serif"
                  fontWeight="700"
                  fontSize="22"
                  fill="#F5E9D7"
                  letterSpacing="2"
                >
                  P
                </text>
              </svg>
            </div>
            <div>
              <div className="pad-brand-name">PADMANAG</div>
              <div className="pad-brand-sub">Party Hall · Mallathahalli, Bengaluru</div>
            </div>
          </div>

          <h1 className="pad-hero-title">
            Party Hall <span className="pad-hero-italic">Catering</span> Calculator
          </h1>
          <p className="pad-hero-tag">
            Menu + Hall, all-inclusive. Pick your guest count — see live pricing across our four
            signature packages.
          </p>

        </div>
      </header>

      {/* SLIDER PANEL */}
      <section className="pad-slider-section" data-testid="pad-slider-section">
        <div className="pad-slider-card">
          <div className="pad-slider-top">
            <div>
              <div className="pad-slider-label">Number of guests</div>
              <div className="pad-slider-hint">Slide to adjust · range 50 – 500</div>
            </div>
            <div className="pad-slider-count" data-testid="pad-guest-count">
              {guests}
              <div className="pad-bracket-pill" data-testid="pad-bracket-pill">
                {bracket}
              </div>
            </div>
          </div>

          <Slider
            value={[guests]}
            min={50}
            max={500}
            step={10}
            onValueChange={(v) => setGuests(v[0])}
            className="pad-slider"
            data-testid="pad-guest-slider"
          />

          <div className="pad-slider-ends">
            <span>50</span>
            <span>275</span>
            <span>500</span>
          </div>
        </div>
      </section>

      {/* CARDS */}
      <section className="pad-cards-wrap">
        <div className="pad-cards" data-testid="pad-cards-grid">
          {computed.map((t) => (
            <article
              key={t.key}
              className={`pad-card ${t.featured ? "pad-card-featured" : ""}`}
              data-testid={`pad-card-${t.key}`}
              style={{ "--tier-accent": t.accent }}
            >
              {t.featured && <div className="pad-card-ribbon">Most Picked</div>}

              <header className="pad-card-head">
                <div className="pad-tier-label">{t.name}</div>
                <div className="pad-tier-tag">{t.tagline}</div>
              </header>

              <div className="pad-card-totals">
                <div className="pad-grand-row">
                  <span className="pad-grand-label">Grand total</span>
                  <span
                    className="pad-grand-value"
                    data-testid={`pad-total-${t.key}`}
                  >
                    {formatINR(t.total)}
                  </span>
                </div>

                <div className="pad-mini-rows">
                  <div className="pad-mini-row pad-effective">
                    <span>Per guest (incl. hall)</span>
                    <span data-testid={`pad-perhead-${t.key}`}>
                      {formatINR(Math.round(t.perHead))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pad-menu">
                <div className="pad-menu-title">Menu Inclusions</div>
                <ul className="pad-menu-list">
                  {t.menu.map((item, i) => (
                    <li key={i}>
                      <Check size={14} strokeWidth={2.2} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="https://wa.me/919845726257"
                target="_blank"
                rel="noopener noreferrer"
                className="pad-card-cta"
                data-testid={`pad-cta-${t.key}`}
              >
                Enquire on WhatsApp
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* FOOTER / CONTACT */}
      <footer className="pad-footer" data-testid="pad-footer">
        <div className="pad-footer-inner">
          <div className="pad-footer-brand">
            <div className="pad-footer-title">PADMANAG</div>
            <div className="pad-footer-sub">Where celebrations come alive</div>
          </div>

          <div className="pad-footer-grid">
            <a
              className="pad-footer-item"
              href="tel:9845726257"
              data-testid="pad-contact-phone"
            >
              <Phone size={16} strokeWidth={1.6} />
              <div>
                <div className="pad-footer-key">Call</div>
                <div className="pad-footer-val">+91 98457 26257</div>
              </div>
            </a>

            <a
              className="pad-footer-item"
              href="https://wa.me/919845726257"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pad-contact-whatsapp"
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              <div>
                <div className="pad-footer-key">WhatsApp</div>
                <div className="pad-footer-val">Message us</div>
              </div>
            </a>

            <div className="pad-footer-item">
              <MapPin size={16} strokeWidth={1.6} />
              <div>
                <div className="pad-footer-key">Visit</div>
                <div className="pad-footer-val">
                  60 Feet Road, NGK & M.P.M Layout, Mallathahalli Lake, 9th Block, Bengaluru —
                  560056
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pad-footer-bottom">
          <span>© {new Date().getFullYear()} Padmanag · Party Hall</span>
          <span className="pad-footer-bottom-sep">·</span>
          <span>Catering calculator — indicative pricing</span>
        </div>
      </footer>
    </div>
  );
}
